// src/context/SettingsContext.js
// Fournit les paramètres globaux (logo, couleur, support) + applique le thème
// Maxula

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { settingsAPI } from '../services/api';

const SettingsContext = createContext({ settings: null, refresh: () => {} });
export const useSettings = () => useContext(SettingsContext);

// ---- Helpers de teinte (dérive des nuances claires/foncées depuis la couleur admin) ----
const _clamp = (n) => Math.max(0, Math.min(255, Math.round(n)));
const _hexToRgb = (hex) => {
  const h = String(hex || '').replace('#', '').trim();
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(full || '000000', 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};
const _rgbToHex = ({ r, g, b }) =>
  '#' + [r, g, b].map((v) => _clamp(v).toString(16).padStart(2, '0')).join('');
// mélange hex1 vers hex2 selon w (0..1)
const _mix = (hex1, hex2, w) => {
  const a = _hexToRgb(hex1), b = _hexToRgb(hex2);
  return _rgbToHex({ r: a.r + (b.r - a.r) * w, g: a.g + (b.g - a.g) * w, b: a.b + (b.b - a.b) * w });
};
const _lighten = (hex, w) => _mix(hex, '#ffffff', w);
const _darken = (hex, w) => _mix(hex, '#000000', w);
const _rgba = (hex, a) => { const { r, g, b } = _hexToRgb(hex); return `rgba(${r},${g},${b},${a})`; };

const applyTheme = (s) => {
  if (!s) return;
  const id = 'maxula-theme-override';
  let style = document.getElementById(id);
  if (!style) {
    style = document.createElement('style');
    style.id = id;
    document.head.appendChild(style);
  }

  const p = s.primaryColor || '#E11D79';        // couleur principale (rose de marque)
  const p2 = s.secondaryColor || _darken(p, 0.12); // hover / nuance foncée
  const pDark = _darken(p, 0.45);               // accent foncé (ex-#7a1147)
  const tint1 = _lighten(p, 0.93);              // fond très clair (ex-#fff3f8 / pink-50)
  const tint2 = _lighten(p, 0.88);              // fond clair (ex-#fde7ef / pink-100)
  const tintBorder = _lighten(p, 0.80);         // bordures claires (pink-100/200/300)

  // Surcharge runtime de TOUTE la famille "rose de marque" : classes Tailwind
  // primary-*, classes pink-*, ET valeurs codées en dur (#e11d7b, #7a1147, tints).
  // -> un changement de couleur admin se propage partout dans le site.
  style.innerHTML = `
    /* === Aplat couleur principale === */
    .bg-primary-600,.bg-primary-500,.bg-pink-500,
    .bg-\\[\\#e11d7b\\],.bg-\\[\\#e11d79\\]{background-color:${p} !important;}
    .text-primary-600,.text-primary-700,.text-pink-600,
    .text-\\[\\#e11d7b\\],.text-\\[\\#e11d79\\]{color:${p} !important;}
    .border-primary-600,.border-primary-500,.border-primary-300,
    .border-\\[\\#e11d7b\\]{border-color:${p} !important;}
    .ring-primary-500:focus,.focus\\:ring-primary-500:focus{--tw-ring-color:${p} !important;}
    .from-primary-600,.from-\\[\\#e11d7b\\]{--tw-gradient-from:${p} !important;}
    .to-\\[\\#e11d7b\\]{--tw-gradient-to:${p} !important;}

    /* === Hover / nuance foncée === */
    .hover\\:bg-primary-700:hover,.hover\\:bg-primary-600:hover,
    .bg-\\[\\#c91469\\],.bg-\\[\\#c4156a\\],
    .hover\\:bg-\\[\\#e11d7b\\]:hover{background-color:${p2} !important;}
    .hover\\:text-\\[\\#e11d7b\\]:hover{color:${p} !important;}
    .hover\\:border-\\[\\#e11d7b\\]:hover,.hover\\:border-pink-400:hover{border-color:${p} !important;}

    /* === Accent foncé (texte type #7a1147) === */
    .text-\\[\\#7a1147\\]{color:${pDark} !important;}
    .bg-\\[\\#7a1147\\]{background-color:${pDark} !important;}

    /* === Fonds & bordures clairs (tints dérivées) === */
    .bg-pink-50,.bg-\\[\\#fff3f8\\]{background-color:${tint1} !important;}
    .bg-pink-100,.bg-\\[\\#fde7ef\\],.hover\\:bg-pink-100:hover{background-color:${tint2} !important;}
    .border-pink-100,.border-pink-200,.border-pink-300{border-color:${tintBorder} !important;}

    /* === Variantes avec opacité (ex: voile du hero mobile) === */
    .bg-\\[\\#e11d7b\\]\\/75{background-color:${_rgba(p, 0.75)} !important;}
    .bg-pink-50\\/80{background-color:${_rgba(tint1, 0.8)} !important;}
    .bg-pink-50\\/60{background-color:${_rgba(tint1, 0.6)} !important;}
  `;
  if (s.siteName) document.title = s.siteName;
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const res = await settingsAPI.get();
      const s = res.data?.data || null;
      setSettings(s);
      applyTheme(s);
    } catch (e) {
      // valeurs par défaut si indisponible
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <SettingsContext.Provider value={{ settings, refresh }}>
      {children}
    </SettingsContext.Provider>
  );
};

export default SettingsContext;
